/**
 * Staff names are two fields, first and last, exactly like customers.
 *
 * One box gets you a first name and nothing else, which is how the owner
 * ended up with "Eilon" and "Maya S" on his own staff records. The database
 * keeps `full_name` as a generated column, so nothing ever writes it.
 */

export type NameParts = { first: string; last: string };

/** Tidy a typed name: trim, and collapse runs of whitespace. */
export function tidyName(value: string | null | undefined): string {
  return String(value ?? "")
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * Split a single name on the FIRST space.
 *
 * Everything before it is the first name, the whole remainder is the last
 * name, so "Adi Ben Harush" becomes "Adi" and "Ben Harush", never
 * "Adi Ben" and "Harush".
 */
export function splitName(value: string | null | undefined): NameParts {
  const tidy = tidyName(value);
  const gap = tidy.indexOf(" ");
  if (gap === -1) return { first: tidy, last: "" };
  return { first: tidy.slice(0, gap), last: tidy.slice(gap + 1).trim() };
}

/** How the two parts read together, matching the generated column. */
export function joinName(
  first: string | null | undefined,
  last: string | null | undefined,
): string {
  return `${tidyName(first)} ${tidyName(last)}`.trim();
}

/** True while we are still missing either half of somebody's name. */
export function needsNameGate(
  profile: { first_name?: string | null; last_name?: string | null } | null | undefined,
): boolean {
  return !tidyName(profile?.first_name) || !tidyName(profile?.last_name);
}
